import { useAssetStore } from '@/stores/api/asset';
import { useLedPanelStore } from '@/stores/api/ledpanel';
import { useTimelinePlayerStore } from '@/stores/ui/timelinePlayer';
import { render } from '@/threejs/core/scene';
import * as THREE from 'three';
import { textureManager } from '../textureManager';

import { useDataValidation } from '@/composables/useDataValidation';
import { resolveInputSource } from '@/threejs/canvas-patterns';
import { drawStatusMessage } from '@/threejs/canvas-patterns/utils/draw-status-message';
import { BaseObject, applyProperties } from './base';

export class Led extends BaseObject {
  constructor(createProperties) {
    super();
    const group = this;
    let panelWidth = createProperties.panelWidth || 0.5;
    let panelHeight = createProperties.panelHeight || 0.5;
    const panelDepth = 0.05;
    this.userData.spacing = createProperties.spacing || 0.01;

    let rows = Number(createProperties.verticalSize) || 5;
    let columns = Number(createProperties.horizontalSize) || 5;
    let currentVideoUrl = null;
    let isVideo = true;
    let horizontalCurvature = createProperties.horizontalCurvature || 0;
    let verticalCurvature = createProperties.verticalCurvature || 0;

    this._sourceSeq = 0;
    this.userData.uvOffsetX = createProperties.uvOffsetX || 0;
    this.userData.uvOffsetY = createProperties.uvOffsetY || 0;
    this.userData.uvScaleX = createProperties.uvScaleX || 1;
    this.userData.uvScaleY = createProperties.uvScaleY || 1;

    const { sanitizeNumber } = useDataValidation();

    // Initialize power and gain
    this.userData.power = createProperties.power ?? true;
    this.userData.gain = sanitizeNumber(createProperties.gain ?? 0.5, { min: 0, max: 1, fallback: 0.5 });

    // Initialize size mode properties
    this.userData.sizeMode = createProperties.sizeMode || 'tiles';
    this.userData.roundTiles = createProperties.roundTiles || 'down';
    this.userData.totalWidth = createProperties.totalWidth || panelWidth * columns;
    this.userData.totalHeight = createProperties.totalHeight || panelHeight * rows;

    let resolutionHeight = createProperties.resolutionHeight || 128;
    let resolutionWidth = createProperties.resolutionWidth || 128;

    let video;

    // Create canvas for texture rendering
    // Canvas size is based on total resolution of all tiles
    let canvasWidth = resolutionWidth * columns;
    let canvasHeight = resolutionHeight * rows;
    let canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    let ctx = canvas.getContext('2d');

    // Create texture from canvas
    let texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.NearestFilter;
    texture.magFilter = THREE.NearestFilter;
    texture.generateMipmaps = false;
    texture.colorSpace = THREE.SRGBColorSpace;

    let isThisVideoPlaying = false;

    // Function to clear canvas
    const clearCanvas = () => {
      ctx.fillStyle = 'black';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    };

    this.play = () => {
      isThisVideoPlaying = true;
      setupVideo(currentVideoUrl);
    };
    this.stop = () => {
      isThisVideoPlaying = false;
      if (video) {
        video.pause();
      }
    };

    this.switchLedPanel = (ledPanel) => {
      // Implement the logic to switch the LED panel

      resolutionHeight = ledPanel.resolution_height || 128;
      resolutionWidth = ledPanel.resolution_width || 128;

      const oldPanelWidth = panelWidth;
      const oldPanelHeight = panelHeight;

      if (ledPanel.panel_width_mm != panelWidth || ledPanel.panel_height_mm != panelHeight) {
        panelWidth = ledPanel.panel_width_mm / 1000 || 0.5;
        panelHeight = ledPanel.panel_height_mm / 1000 || 0.5;

        // If in dimensions mode, recalculate rows/columns based on new panel size
        if (this.userData.sizeMode === 'dimensions') {
          const totalWidth = this.userData.totalWidth || oldPanelWidth * columns;
          const totalHeight = this.userData.totalHeight || oldPanelHeight * rows;

          // Calculate number of tiles based on dimensions and new panel size
          const rawColumns = totalWidth / panelWidth;
          const rawRows = totalHeight / panelHeight;

          // Round based on user preference
          const roundMethod = this.userData.roundTiles === 'up' ? Math.ceil : Math.floor;
          columns = Math.max(1, roundMethod(rawColumns));
          rows = Math.max(1, roundMethod(rawRows));

          // Update the tile counts
          this.userData.horizontalSize = columns;
          this.userData.verticalSize = rows;
        }

        while (panels.length > 0) {
          const panel = panels.pop();
          group.remove(panel);
          disposePanel(panel);
        }
      }

      // Update canvas dimensions
      canvasWidth = resolutionWidth * columns;
      canvasHeight = resolutionHeight * rows;
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // Get a fresh context after resizing (canvas resize clears the context)
      ctx = canvas.getContext('2d');

      // Clear the resized canvas (now with updated dimensions)
      ctx.fillStyle = 'black';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // Recreate texture from the resized canvas
      texture.dispose(); // Clean up old texture
      texture = new THREE.CanvasTexture(canvas);
      texture.minFilter = THREE.NearestFilter;
      texture.magFilter = THREE.NearestFilter;
      texture.generateMipmaps = false;
      texture.colorSpace = THREE.SRGBColorSpace;

      //ledPanel.brightness_nits

      // Update grid with new panel dimensions (this will assign the new texture to panels)
      updateGrid();

      // Refresh the current input source to redraw content at new resolution
      group.refreshInputSource();
    };

    // Function to stop video animation
    const stopVideoAnimation = () => {
      if (group.userData.videoAnimationId) {
        cancelAnimationFrame(group.userData.videoAnimationId);
        group.userData.videoAnimationId = null;
      }
    };

    // Function to setup video with HLS support
    const setupVideo = (url, initialTime = 0) => {
      stopVideoAnimation();
      if (video) {
        video.pause();
      }
      currentVideoUrl = url;
      isVideo = true;
      video = textureManager.getVideoElement(url);
      if (!video) {
        drawError('Failed to load asset');
        return;
      }

      // Video starts paused — only the play button should start it.
      video.autoplay = false;
      video.loop = false;
      video.muted = true;
      video.playsInline = true;
      video.pause();

      video.addEventListener(
        'error',
        () => {
          drawError('Failed to load asset');
        },
        { once: true }
      );

      // Set initial video time if provided
      if (initialTime > 0 && video.readyState >= 2) {
        video.currentTime = initialTime;
      }

      // Sync with timeline state
      const timelinePlayerStore = useTimelinePlayerStore();

      // Track state for initialization and looping
      let wasInRange = false;
      let videoInitialized = false;

      // Create a render loop to update the canvas with video frames and sync with timeline
      const updateCanvasWithVideo = () => {
        if (!video) return;
        group.userData.videoAnimationId = requestAnimationFrame(updateCanvasWithVideo);
        const currentTime = timelinePlayerStore.currentTime;
        const mediaStartTime = group.userData.mediaStartTime || 0;
        const mediaStart = group.userData.mediaStart || 0;
        const mediaDuration = group.userData.mediaDuration || 10;
        const mediaEndTime = mediaStartTime + mediaDuration;
        const isInRange = currentTime >= mediaStartTime && currentTime <= mediaEndTime;

        // Initialize video position when entering the media block range
        if (isInRange && !wasInRange && !videoInitialized) {
          videoInitialized = true;
          if (video.readyState >= 2) {
            video.currentTime = mediaStart;
          }
        }

        // Reset initialization flag when exiting range so it can reinit on next entry
        if (!isInRange && wasInRange) {
          videoInitialized = false;
        }

        // Handle play/pause based on timeline state and block range
        if (timelinePlayerStore.isPlaying && isThisVideoPlaying && isInRange) {
          if (video.paused && video.readyState >= video.HAVE_CURRENT_DATA) {
            video.play().catch(() => {});
          }
          // Check if video reached its end while playing (natural loop)
          if (video.duration && video.currentTime >= video.duration - 0.1) {
            video.currentTime = mediaStart;
          }
        } else {
          if (!video.paused) {
            video.pause();
          }
        }

        // Draw frame if video is ready and in range
        if (video && video.readyState >= video.HAVE_CURRENT_DATA && isInRange) {
          ctx.clearRect(0, 0, canvasWidth, canvasHeight);
          ctx.drawImage(video, 0, 0, canvasWidth, canvasHeight);
          texture.needsUpdate = true;
          render();
        } else if (!isInRange) {
          clearCanvas();
          texture.needsUpdate = true;
          render();
        }

        wasInRange = isInRange;
      };

      // Start animation and playback on canplay
      video.addEventListener(
        'canplay',
        () => {
          if (!group.userData.videoAnimationId) updateCanvasWithVideo();
        },
        { once: true }
      );

      // If already ready, start immediately
      if (video.readyState >= video.HAVE_CURRENT_DATA) {
        updateCanvasWithVideo();
      }

      // Update all panels with the new texture
      panels.forEach((panel) => {
        const frontPlane = panel.children[1];
        frontPlane.material.map = texture;
        frontPlane.material.needsUpdate = true;
      });
    };

    const drawError = (message) => {
      drawStatusMessage(ctx, canvasWidth, canvasHeight, message, '#a00');
      texture.needsUpdate = true;
      panels.forEach((panel) => {
        const frontPlane = panel.children[1];
        frontPlane.material.map = texture;
        frontPlane.material.needsUpdate = true;
      });
      render();
    };

    const showLoading = () => {
      clearCanvas();
      drawStatusMessage(ctx, canvasWidth, canvasHeight, 'Loading...');
      texture.needsUpdate = true;
      panels.forEach((panel) => {
        const frontPlane = panel.children[1];
        frontPlane.material.map = texture;
        frontPlane.material.needsUpdate = true;
      });
      render();
    };

    const showSelectingAsset = () => {
      stopVideoAnimation();
      currentVideoUrl = null;
      isVideo = false;
      clearCanvas();
      drawStatusMessage(ctx, canvasWidth, canvasHeight, 'Select an asset');
      texture.needsUpdate = true;
      panels.forEach((panel) => {
        const frontPlane = panel.children[1];
        frontPlane.material.map = texture;
        frontPlane.material.needsUpdate = true;
      });
      render();
    };

    const setupAssetContent = (asset) => {
      showLoading();
      if (asset.type === 'IM') {
        setupImage(asset.sourceUrl);
      } else if (asset.sourceUrl) {
        currentVideoUrl = asset.sourceUrl;
        this.play && this.play();
      }
    };
    // Function to setup image
    const setupImage = async (url) => {
      stopVideoAnimation(); // Stop any running video animation
      currentVideoUrl = url;
      isVideo = false;

      let imageTexture;
      try {
        imageTexture = await textureManager.getImageTexture(url);
      } catch {
        drawError('Failed to load asset');
        return;
      }

      const image = imageTexture.image;
      clearCanvas();

      // Draw image to full canvas
      ctx.drawImage(image, 0, 0, canvasWidth, canvasHeight);

      // Update the canvas texture
      texture.generateMipmaps = false;
      texture.minFilter = THREE.NearestFilter;
      texture.magFilter = THREE.NearestFilter;

      texture.needsUpdate = true;

      // Update all panels with the canvas texture
      panels.forEach((panel) => {
        const frontPlane = panel.children[1];
        frontPlane.material.map = texture;
        frontPlane.material.needsUpdate = true;
      });
      render();
    };

    this.setUvScale = (x, y) => {
      this.userData.uvScaleX = Number(x);
      this.userData.uvScaleY = Number(y);
      texture.repeat.set(this.userData.uvScaleX, this.userData.uvScaleY);
      texture.needsUpdate = true;
    };

    this.setUvOffset = (x, y) => {
      this.userData.uvOffsetX = Number(x);
      this.userData.uvOffsetY = Number(y);
      texture.offset.set(this.userData.uvOffsetX, this.userData.uvOffsetY);
      texture.needsUpdate = true;
    };

    const drawPattern = (name, options) => {
      stopVideoAnimation();
      currentVideoUrl = null;
      isVideo = false;
      clearCanvas();

      const pattern = resolveInputSource(name);
      pattern.draw(ctx, canvasWidth, canvasHeight, { ...group.userData, ...options });

      texture.needsUpdate = true;
      panels.forEach((panel) => {
        const frontPlane = panel.children[1];
        frontPlane.material.map = texture;
        frontPlane.material.needsUpdate = true;
      });
    };

    const calculatePanelPosition = (row, col) => {
      const centerX = ((columns - 1) * (panelWidth + this.userData.spacing)) / 2;
      const centerY = ((rows - 1) * (panelHeight + this.userData.spacing)) / 2;

      const baseX = col * (panelWidth + this.userData.spacing);
      const baseY = row * (panelHeight + this.userData.spacing);

      const normalizedX = centerX !== 0 ? (baseX - centerX) / centerX : 0;
      const normalizedY = centerY !== 0 ? (baseY - centerY) / centerY : 0;

      let finalX = baseX - centerX;
      let finalY = baseY - centerY;
      let finalZ = 0;

      horizontalCurvature = Number(horizontalCurvature) || 0;
      if (horizontalCurvature !== 0) {
        const totalCurveRadians = (horizontalCurvature * Math.PI) / 180;
        const hCurve = (totalCurveRadians * 2) / Math.PI;
        const angleH = (normalizedX * Math.PI * hCurve) / 2;
        const radiusH = centerX / ((hCurve * Math.PI) / 2);

        finalX = Math.sin(angleH) * radiusH;
        finalZ = (1 - Math.cos(angleH)) * radiusH;
      }

      verticalCurvature = Number(verticalCurvature) || 0;
      if (verticalCurvature !== 0) {
        const totalCurveRadiansVertical = (verticalCurvature * Math.PI) / 180;
        const vCurve = (totalCurveRadiansVertical * 2) / Math.PI;
        const angleV = (normalizedY * Math.PI * vCurve) / 2;
        const radiusV = centerY / ((vCurve * Math.PI) / 2);

        finalY = Math.sin(angleV) * radiusV;
        finalZ += (1 - Math.cos(angleV)) * radiusV;
      }

      const rotationX = verticalCurvature !== 0 ? (normalizedY * verticalCurvature * Math.PI) / 180 : 0;
      const rotationY = horizontalCurvature !== 0 ? (-normalizedX * horizontalCurvature * Math.PI) / 180 : 0;

      return {
        position: [finalX, finalY, finalZ],
        rotation: [rotationX, rotationY, 0],
      };
    };

    const createPanel = (row, col) => {
      const panelGroup = new THREE.Group();

      const planeFrontMaterial = new THREE.MeshStandardMaterial({
        color: 0xffffff,
      });
      const planeMaterial = new THREE.MeshStandardMaterial({
        color: 0x000000,
      });
      // Create the panel mesh
      const panel = new THREE.Mesh(new THREE.BoxGeometry(panelWidth, panelHeight, panelDepth), planeMaterial);
      panel.castShadow = true;
      // Exclude LED screens from projector gradient overlay to reduce texture unit usage
      panel.userData.projectorOverlayIgnore = true;

      panelGroup.add(panel);

      // Create the front plane with custom UVs
      const planeGeometry = new THREE.PlaneGeometry(panelWidth, panelHeight);

      const frontPlane = new THREE.Mesh(planeGeometry, planeFrontMaterial);
      frontPlane.userData.type = 'led';
      // Exclude LED screens from projector gradient overlay to reduce texture unit usage
      frontPlane.userData.projectorOverlayIgnore = true;
      frontPlane.position.z = panelDepth / 2 + 0.01; // Slightly in front of the panel
      frontPlane.visible = this.userData.power;
      panelGroup.add(frontPlane);

      // Update UVs for this panel
      updatePanelUVs(frontPlane, row, col);

      return panelGroup;
    };

    const updatePanelUVs = (frontPlane, row, col) => {
      const planeGeometry = frontPlane.geometry;
      // Calculate UV coordinates for this panel
      const uStart = col / columns;
      const vStart = row / rows;
      const uEnd = (col + 1) / columns;
      const vEnd = (row + 1) / rows;

      // Set UV coordinates for each vertex (flipping V coordinates for each panel)
      planeGeometry.attributes.uv.array[0] = uStart; // bottom-left
      planeGeometry.attributes.uv.array[1] = vEnd;
      planeGeometry.attributes.uv.array[2] = uEnd; // bottom-right
      planeGeometry.attributes.uv.array[3] = vEnd;
      planeGeometry.attributes.uv.array[4] = uStart; // top-left
      planeGeometry.attributes.uv.array[5] = vStart;
      planeGeometry.attributes.uv.array[6] = uEnd; // top-right
      planeGeometry.attributes.uv.array[7] = vStart;

      planeGeometry.attributes.uv.needsUpdate = true;
    };

    const disposePanel = (panel) => {
      panel.children.forEach((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    };

    const panels = [];

    // Helper function to apply gain to all panels
    const applyGainToAllPanels = () => {
      if (this.userData.gain > 0) {
        panels.forEach((panel) => {
          const frontPlane = panel.children[1];
          if (frontPlane && frontPlane.material) {
            frontPlane.material.emissiveMap = frontPlane.material.map;
            frontPlane.material.emissive = new THREE.Color(0xffffff);
            frontPlane.material.emissiveIntensity = this.userData.gain;
            frontPlane.material.needsUpdate = true;
          }
        });
      } else {
        panels.forEach((panel) => {
          const frontPlane = panel.children[1];
          if (frontPlane && frontPlane.material) {
            frontPlane.material.emissiveMap = null;
            frontPlane.material.emissive = new THREE.Color(0x000000);
            frontPlane.material.emissiveIntensity = 0;
            frontPlane.material.needsUpdate = true;
          }
        });
      }
    };

    const updateCanvasSize = () => {
      // Update canvas dimensions based on total resolution
      canvasWidth = resolutionWidth * columns;
      canvasHeight = resolutionHeight * rows;
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // Get a fresh context after resizing
      ctx = canvas.getContext('2d');

      // Recreate texture from the resized canvas
      texture.dispose(); // Clean up old texture
      texture = new THREE.CanvasTexture(canvas);
      texture.minFilter = THREE.NearestFilter;
      texture.magFilter = THREE.NearestFilter;
      texture.generateMipmaps = false;
      texture.colorSpace = THREE.SRGBColorSpace;

      // Update all panels with the new texture
      panels.forEach((panel) => {
        const frontPlane = panel.children[1];
        frontPlane.material.map = texture;
        frontPlane.material.needsUpdate = true;
      });

      // Reapply gain after texture update
      applyGainToAllPanels();
    };

    const updateGrid = () => {
      // Update canvas size when grid dimensions change
      updateCanvasSize();

      // Remove extra panels if needed
      while (panels.length > rows * columns) {
        const panel = panels.pop();
        group.remove(panel);
        disposePanel(panel);
      }

      // Add new panels if needed
      for (let i = panels.length; i < rows * columns; i++) {
        const row = Math.floor(i / columns);
        const col = i % columns;
        const panel = createPanel(row, col);
        panels.push(panel);
        group.add(panel);
      }

      // Update positions and UVs of all panels
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < columns; col++) {
          const index = row * columns + col;
          const panel = panels[index];
          const { position, rotation } = calculatePanelPosition(row, col);

          panel.position.set(position[0], position[1], position[2]);
          panel.rotation.set(rotation[0], rotation[1], rotation[2]);

          // Update UVs for existing panels
          const frontPlane = panel.children[1];
          updatePanelUVs(frontPlane, row, col);
        }
      }

      group.refreshInputSource();
      // Reapply gain after grid update
      applyGainToAllPanels();
    };

    // Method to set an image texture
    group.setImageTexture = (imageUrl) => {
      setupImage(imageUrl);
    };

    // Method to set a video texture
    group.setVideoTexture = (videoUrl) => {
      setupVideo(videoUrl);
    };

    // Method to refresh Input Source based on current state
    group.refreshInputSource = () => {
      const name = group.userData.currentInputSource;
      if (name === 'source') {
        if (group.userData.sourceUrl) {
          setupAssetContent(group.userData);
        } else {
          showSelectingAsset();
        }
      } else if (name === 'colorGrid') {
        drawPattern('colorGrid', { gridDivsX: columns, gridDivsY: rows });
      } else {
        drawPattern(name || 'calibrationGrid');
      }
    };

    group.setPanelRows = (newRows) => {
      if (newRows <= 0) return;
      rows = newRows;
      updateGrid();
    };

    group.setPanelColumns = (newColumns) => {
      if (newColumns <= 0) return;
      columns = newColumns;
      updateGrid();
    };

    group.setHorizontalCurvature = (newCurvature) => {
      horizontalCurvature = newCurvature;
      updateGrid();
    };

    group.setVerticalCurvature = (newCurvature) => {
      verticalCurvature = newCurvature;
      updateGrid();
    };

    // Initialize with default grid
    updateGrid();

    // Add cleanup event listener
    group.addEventListener('removed', () => {
      // Cancel any running video animation
      if (group.userData.videoAnimationId) {
        cancelAnimationFrame(group.userData.videoAnimationId);
        group.userData.videoAnimationId = null;
      }
      // Stop and release video
      if (video) {
        video.pause();
        video = null;
      }
      // Dispose canvas texture
      if (texture) {
        texture.dispose();
        texture = null;
      }
      // Dispose panel geometries and materials
      panels.forEach((panel) => disposePanel(panel));
      panels.length = 0;
    });

    group.applyProperties = async (properties) => {
      applyProperties(group, properties, false);

      // Skip LED grid rebuild during gizmo drag to prevent detachment
      if (this.isTransforming) return;

      if ('libraryCode' in properties) {
        if (this.userData.libraryCode !== properties.libraryCode) {
          group.userData.libraryCode = properties.libraryCode;
          //
          const ledPanelStore = useLedPanelStore();

          ledPanelStore.fetch(properties.libraryCode).then((ledPanel) => {
            this.switchLedPanel(ledPanel);
          });
        }
      }

      // Handle size mode changes
      if ('sizeMode' in properties) {
        this.userData.sizeMode = properties.sizeMode;
      }

      if ('roundTiles' in properties) {
        this.userData.roundTiles = properties.roundTiles;
      }

      if ('power' in properties) {
        this.userData.power = properties.power;
        panels.forEach((panel) => {
          const frontPlane = panel.children[1];
          if (frontPlane) {
            frontPlane.visible = properties.power;
          }
        });
      }

      if ('gain' in properties) {
        this.userData.gain = sanitizeNumber(properties.gain);
        // Apply gain to all panels using helper function
        applyGainToAllPanels();
      }

      // Calculate rows/columns based on size mode
      let calculatedRows = rows;
      let calculatedColumns = columns;

      if (this.userData.sizeMode === 'dimensions') {
        if ('totalWidth' in properties || 'totalHeight' in properties) {
          const totalWidth = properties.totalWidth || this.userData.totalWidth || panelWidth * columns;
          const totalHeight = properties.totalHeight || this.userData.totalHeight || panelHeight * rows;

          this.userData.totalWidth = totalWidth;
          this.userData.totalHeight = totalHeight;

          // Calculate number of tiles based on dimensions
          const rawColumns = totalWidth / panelWidth;
          const rawRows = totalHeight / panelHeight;

          // Round based on user preference
          const roundMethod = this.userData.roundTiles === 'up' ? Math.ceil : Math.floor;
          calculatedColumns = Math.max(1, roundMethod(rawColumns));
          calculatedRows = Math.max(1, roundMethod(rawRows));

          // Update the tile counts for display/storage
          this.userData.horizontalSize = calculatedColumns;
          this.userData.verticalSize = calculatedRows;
        }
      } else {
        // Tile count mode - use direct values
        if ('horizontalSize' in properties) {
          this.userData.horizontalSize = properties.horizontalSize;
          calculatedColumns = properties.horizontalSize;
        }

        if ('verticalSize' in properties) {
          this.userData.verticalSize = properties.verticalSize;
          calculatedRows = properties.verticalSize;
        }
      }

      // Apply the calculated values if they changed
      if (calculatedColumns !== columns) {
        group.setPanelColumns(calculatedColumns);
      }

      if (calculatedRows !== rows) {
        group.setPanelRows(calculatedRows);
      }

      if ('horizontalCurvature' in properties) {
        if (this.userData.horizontalCurvature !== properties.horizontalCurvature) {
          this.userData.horizontalCurvature = properties.horizontalCurvature;
          group.setHorizontalCurvature(properties.horizontalCurvature);
        }
      }

      if ('verticalCurvature' in properties) {
        if (this.userData.verticalCurvature !== properties.verticalCurvature) {
          this.userData.verticalCurvature = properties.verticalCurvature;
          group.setVerticalCurvature(properties.verticalCurvature);
        }
      }

      if ('spacing' in properties) {
        if (this.userData.spacing !== properties.spacing) {
          this.userData.spacing = properties.spacing;
          updateGrid();
        }
      }

      if ('uvScaleX' in properties && 'uvScaleY' in properties) {
        if (this.userData.uvScaleX !== properties.uvScaleX || this.userData.uvScaleY !== properties.uvScaleY) {
          this.setUvScale(properties.uvScaleX, properties.uvScaleY);
        }
      }

      if ('uvOffsetX' in properties && 'uvOffsetY' in properties) {
        if (this.userData.uvOffsetX !== properties.uvOffsetX || this.userData.uvOffsetY !== properties.uvOffsetY) {
          this.setUvOffset(properties.uvOffsetX, properties.uvOffsetY);
        }
      }

      // Fetch source asset and display it when source changes
      if ('source' in properties && properties.inputSource === 'source' && properties.source) {
        if (this.userData.source !== properties.source) {
          this.userData.source = properties.source;
          const seq = ++this._sourceSeq;
          try {
            const assetStore = useAssetStore();
            const asset = await assetStore.fetch(properties.source);
            if (seq !== this._sourceSeq) return;
            if (asset?.latest_version) {
              this.userData.sourceUrl = asset.latest_version.playback_url || asset.latest_version.file;
              this.userData.sourceAssetType = asset.asset_type;
              group.userData.currentInputSource = 'source';
              this.userData.inputSource = 'source';
              setupAssetContent({ type: this.userData.sourceAssetType, sourceUrl: this.userData.sourceUrl });
            }
          } catch (e) {
            console.warn('Failed to fetch LED source asset:', properties.source, e);
          }
        }
      }

      if ('solidColor' in properties) {
        group.userData.solidColor = properties.solidColor;
        if (group.userData.currentInputSource === 'solid') {
          drawPattern('solid');
        }
      }

      if ('inputSource' in properties) {
        const inputSourceChanged = this.userData.inputSource !== properties.inputSource;
        const sourceChanged = this.userData.sourceUrl !== (properties.sourceUrl || this.userData.sourceUrl);

        if (inputSourceChanged || (properties.inputSource === 'source' && sourceChanged)) {
          this.userData.inputSource = properties.inputSource;
          group.userData.currentInputSource = properties.inputSource;

          if (properties.inputSource === 'source') {
            if (this.userData.sourceUrl) {
              setupAssetContent({ type: this.userData.sourceAssetType, sourceUrl: this.userData.sourceUrl });
            } else {
              showSelectingAsset();
            }
          } else if (properties.inputSource === 'colorGrid') {
            drawPattern('colorGrid', { gridDivsX: columns, gridDivsY: rows });
          } else {
            drawPattern(properties.inputSource || 'calibrationGrid');
          }
        }
      }

      if ('mediaStartTime' in properties) {
        group.userData.mediaStartTime = properties.mediaStartTime;
      }
      if ('mediaDuration' in properties) {
        group.userData.mediaDuration = properties.mediaDuration;
      }
      if ('mediaStart' in properties) {
        group.userData.mediaStart = properties.mediaStart;
      }
    };

    group.select = () => {
      group.userData.selected = true;
    };
    group.clearSelection = () => {
      group.userData.selected = false;
    };

    // Initialize with default pattern
    drawPattern('calibrationGrid');

    // Apply default gain to all panels after content is set up
    applyGainToAllPanels();

    return group;
  }
}
